import React, { useState } from 'react';
import { ILegalTransactionMeeting } from '../../Interfaces/ILegalTransactionMeeting';
import { ILegalTransactionMeetingEmail } from '../../Interfaces/ILegalTransactionMeetingEmail';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Edit,
  LocationOn,
  OpenInNew,
  Videocam,
} from '@mui/icons-material';
import { LegalTransactionMeetingEdit } from './LegalTransactionMeetingEdit';
import { castWithZeroTimezone } from '../../core/generic_helper';
import { ILegalTransactionTalkRoomDraft } from '../../Interfaces/ILegalTransactionTalkRoom';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { normalizeMeetingLegalClients } from './meetingLegalClient';
import { getLegalClientLabel } from './TalkRoom/talkRoomDisplay';

const formatMeetingDate = (dateValue: string) =>
  new Date(castWithZeroTimezone(dateValue)!).toLocaleDateString('de-DE');

const formatMeetingTime = (dateValue: string) =>
  new Date(castWithZeroTimezone(dateValue)!).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });

interface IProps {
  idLegalTransaction: number;
  idLegalTransactionType: number;
  legalClients: ILegalClient[];
  meetingObject: ILegalTransactionMeeting;
  meetingArray: ILegalTransactionMeeting[];
  setMeetingArray: (meetingArray: ILegalTransactionMeeting[]) => void;
  meetingEmailArray: ILegalTransactionMeetingEmail[];
  setMeetingEmailArray: (
    meetingEmailArray: ILegalTransactionMeetingEmail[]
  ) => void;
  talkRooms: ILegalTransactionTalkRoomDraft[];
  setTalkRooms: React.Dispatch<
    React.SetStateAction<ILegalTransactionTalkRoomDraft[]>
  >;
}

export const LegalTransactionMeetingRow: React.FC<IProps> = props => {
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const [editMeetingObject, setEditMeetingObject] = useState(
    props.meetingObject
  );
  const [editMeetingEmailArray, setEditMeetingEmailArray] = useState(
    props.meetingEmailArray.filter(
      x =>
        x.idLegalTransactionMeeting ===
        props.meetingObject.idLegalTransactionMeeting
    )
  );

  const getTillDate = (): string => {
    const { Meeting_at, DurationInMin } = props.meetingObject;

    // Wenn kein Termin gesetzt oder Dauer fehlt, leeren String zurückgeben
    if (!Meeting_at || DurationInMin === null || DurationInMin === undefined) {
      return '';
    }

    // Datum ohne Zeitzonen-Verschiebung parsen
    const meetingTime = new Date(castWithZeroTimezone(Meeting_at)!);
    // Eine Kopie anlegen, damit das Original nicht mutiert wird
    const endTime = new Date(meetingTime.getTime());
    endTime.setMinutes(meetingTime.getMinutes() + DurationInMin);

    return endTime.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenEdit = () => {
    setEditMeetingObject({
      ...props.meetingObject,
      LegalClientArray:
        props.meetingObject.LegalClientArray !== undefined
          ? normalizeMeetingLegalClients(props.meetingObject.LegalClientArray)
          : normalizeMeetingLegalClients(props.legalClients),
    });
    setEditMeetingEmailArray(
      props.meetingEmailArray.filter(
        x =>
          x.idLegalTransactionMeeting ===
          props.meetingObject.idLegalTransactionMeeting
      )
    );
    setIsOpenEdit(true);
  };

  const handleSave = () => {
    props.setMeetingArray([
      ...props.meetingArray.map(x =>
        x.idLegalTransactionMeeting ===
        editMeetingObject.idLegalTransactionMeeting
          ? editMeetingObject
          : x
      ),
    ]);
    props.setMeetingEmailArray([
      ...props.meetingEmailArray.filter(
        x =>
          x.idLegalTransactionMeeting !==
          editMeetingObject.idLegalTransactionMeeting
      ),
      ...editMeetingEmailArray,
    ]);
    setIsOpenEdit(false);
  };

  const handleRemoveMe = () => {
    props.setMeetingArray([
      ...props.meetingArray.filter(
        x =>
          x.idLegalTransactionMeeting !==
          editMeetingObject.idLegalTransactionMeeting
      ),
    ]);
    props.setMeetingEmailArray([
      ...props.meetingEmailArray.filter(
        x =>
          x.idLegalTransactionMeeting !==
          editMeetingObject.idLegalTransactionMeeting
      ),
    ]);
    setIsOpenEdit(false);
  };

  return (
    <>
      <Dialog open={isConfirmDelete} onClose={() => setIsConfirmDelete(false)}>
        <DialogTitle>Termin absagen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll der Termin &ldquo;
            {props.meetingObject.LegalTransactionMeeting}&rdquo; wirklich
            abgesagt werden? Falls eine Absage-E-Mail konfiguriert ist, wird
            diese an die Mandanten gesendet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDelete(false)}>Abbrechen</Button>
          <Button
            color='error'
            variant='contained'
            onClick={() => {
              setIsConfirmDelete(false);
              handleRemoveMe();
            }}
          >
            Absagen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOpenEdit}
        onClose={() => setIsOpenEdit(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>Termin bearbeiten</DialogTitle>
        <DialogContent dividers>
          <LegalTransactionMeetingEdit
            idLegalTransaction={props.idLegalTransaction}
            idLegalTransactionType={props.idLegalTransactionType}
            legalClients={props.legalClients}
            meetingObject={editMeetingObject}
            setMeetingObject={setEditMeetingObject}
            meetingEmailArray={editMeetingEmailArray}
            setMeetingEmailArray={setEditMeetingEmailArray}
            talkRooms={props.talkRooms}
            setTalkRooms={props.setTalkRooms}
            setAddNew={setIsOpenEdit}
            handleSave={handleSave}
          />
        </DialogContent>
      </Dialog>

      <TableRow>
        <TableCell>
          {formatMeetingDate(props.meetingObject.Meeting_at)}
        </TableCell>
        <TableCell>
          {formatMeetingTime(props.meetingObject.Meeting_at)}
        </TableCell>
        <TableCell>{getTillDate()}</TableCell>
        <TableCell>{props.meetingObject.LegalTransactionMeeting}</TableCell>
        <TableCell>
          <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            flexWrap='wrap'
          >
            {props.meetingObject.idLegalTransactionTalkRoom ? (
              <>
                <Tooltip
                  title={
                    props.meetingObject.TalkRoomObject?.RoomName ??
                    'Online-Raum'
                  }
                >
                  <Chip
                    icon={<Videocam />}
                    label={
                      props.meetingObject.TalkRoomObject?.RoomName ?? 'Online'
                    }
                    size='small'
                    color='primary'
                    variant='outlined'
                  />
                </Tooltip>
                {props.meetingObject.TalkRoomObject?.JoinUrl && (
                  <Tooltip title='Meeting beitreten'>
                    <IconButton
                      size='small'
                      color='primary'
                      component='a'
                      href={props.meetingObject.TalkRoomObject.JoinUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <OpenInNew fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : props.meetingObject.idLocation ? (
              <Tooltip
                title={[
                  props.meetingObject.LocationObject?.Location ?? 'Standort',
                  props.meetingObject.RoomObject?.Name,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <Chip
                  icon={<LocationOn />}
                  label={[
                    props.meetingObject.LocationObject?.Location ?? 'Standort',
                    props.meetingObject.RoomObject?.Name,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  size='small'
                  variant='outlined'
                />
              </Tooltip>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction='row' spacing={0.5} flexWrap='wrap'>
            {(() => {
              const clients = props.meetingObject.LegalClientArray ?? [];
              const maxVisible = 2;
              const visible = clients.slice(0, maxVisible);
              const overflow = clients.length - maxVisible;
              return (
                <>
                  {visible.map(c => (
                    <Chip
                      key={c.idLegalClient}
                      label={getLegalClientLabel(c)}
                      size='small'
                      variant='outlined'
                    />
                  ))}
                  {overflow > 0 && (
                    <Tooltip
                      title={clients
                        .slice(maxVisible)
                        .map(c => getLegalClientLabel(c))
                        .join(', ')}
                    >
                      <Chip label={`+${overflow}`} size='small' />
                    </Tooltip>
                  )}
                  {clients.length === 0 && (
                    <span style={{ color: '#aaa', fontSize: '0.75rem' }}>
                      –
                    </span>
                  )}
                </>
              );
            })()}
          </Stack>
        </TableCell>
        <TableCell>
          {
            props.meetingEmailArray.filter(
              x =>
                x.idLegalTransactionMeeting ===
                props.meetingObject.idLegalTransactionMeeting
            ).length
          }
        </TableCell>
        <TableCell>
          <IconButton size='small' onClick={handleOpenEdit}>
            <Edit />
          </IconButton>
          <IconButton size='small' onClick={() => setIsConfirmDelete(true)}>
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
    </>
  );
};
